export const PROFILE_STORAGE_KEYS = {
  primaryToken: 'token',
  activeToken: 'activeProfileToken',
  secondaryTokens: 'tokens',
};

const isBrowserStorageAvailable = () => typeof window !== 'undefined';

export const parseProfileTokenList = (rawValue) => {
  try {
    const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    if (!Array.isArray(parsed)) return [];

    return [...new Set(
      parsed
        .filter((token) => typeof token === 'string')
        .map((token) => token.trim())
        .filter(Boolean)
    )];
  } catch {
    return [];
  }
};

const parseStoredProfileTokenList = (rawValue) => {
  try {
    const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    if (!Array.isArray(parsed)) {
      return { tokens: [], canPersistSanitizedValue: false };
    }

    return {
      tokens: parseProfileTokenList(parsed),
      canPersistSanitizedValue: true,
    };
  } catch {
    return { tokens: [], canPersistSanitizedValue: false };
  }
};

export const getPrimaryProfileToken = () => {
  if (!isBrowserStorageAvailable()) return '';
  return localStorage.getItem(PROFILE_STORAGE_KEYS.primaryToken) || '';
};

export const getPersistedActiveProfileToken = () => {
  if (!isBrowserStorageAvailable()) return '';
  return localStorage.getItem(PROFILE_STORAGE_KEYS.activeToken) || '';
};

export const getActiveProfileToken = () => {
  if (!isBrowserStorageAvailable()) return '';
  const sessionToken = sessionStorage.getItem(PROFILE_STORAGE_KEYS.primaryToken) || '';
  const persistedActiveToken = getPersistedActiveProfileToken();
  const primaryToken = getPrimaryProfileToken();

  if (primaryToken && !persistedActiveToken && (!sessionToken || sessionToken === primaryToken)) {
    localStorage.setItem(PROFILE_STORAGE_KEYS.activeToken, primaryToken);
  }

  return (
    sessionToken ||
    persistedActiveToken ||
    primaryToken ||
    ''
  );
};

export const setPrimaryProfileToken = (token) => {
  if (!isBrowserStorageAvailable()) return;
  const safeToken = String(token || '').trim();
  if (!safeToken) return;

  localStorage.setItem(PROFILE_STORAGE_KEYS.primaryToken, safeToken);
  localStorage.setItem(PROFILE_STORAGE_KEYS.activeToken, safeToken);
  sessionStorage.setItem(PROFILE_STORAGE_KEYS.primaryToken, safeToken);
};

export const setActiveProfileToken = (token) => {
  if (!isBrowserStorageAvailable()) return;
  const safeToken = String(token || '').trim();
  if (!safeToken) return;

  localStorage.setItem(PROFILE_STORAGE_KEYS.activeToken, safeToken);
  sessionStorage.setItem(PROFILE_STORAGE_KEYS.primaryToken, safeToken);
};

export const getSecondaryProfileTokens = () => {
  if (!isBrowserStorageAvailable()) return [];
  const primaryToken = getPrimaryProfileToken();
  const rawTokens = localStorage.getItem(PROFILE_STORAGE_KEYS.secondaryTokens);
  const parsed = parseStoredProfileTokenList(rawTokens);
  const tokens = parsed.tokens.filter((token) => token !== primaryToken);

  if (parsed.canPersistSanitizedValue) {
    localStorage.setItem(PROFILE_STORAGE_KEYS.secondaryTokens, JSON.stringify(tokens));
  }
  return tokens;
};

export const addSecondaryProfileToken = (token) => {
  if (!isBrowserStorageAvailable()) return [];
  const safeToken = String(token || '').trim();
  const primaryToken = getPrimaryProfileToken();
  if (!safeToken || safeToken === primaryToken) {
    return getSecondaryProfileTokens();
  }

  const tokens = [...new Set([...getSecondaryProfileTokens(), safeToken])];
  localStorage.setItem(PROFILE_STORAGE_KEYS.secondaryTokens, JSON.stringify(tokens));
  return tokens;
};

export const removeSecondaryProfileToken = (token) => {
  if (!isBrowserStorageAvailable()) return [];
  const safeToken = String(token || '').trim();
  const tokens = getSecondaryProfileTokens().filter((item) => item !== safeToken);
  localStorage.setItem(PROFILE_STORAGE_KEYS.secondaryTokens, JSON.stringify(tokens));

  if (getPersistedActiveProfileToken() === safeToken) {
    setActiveProfileToken(getPrimaryProfileToken());
  }

  return tokens;
};

export const clearProfileSession = () => {
  if (!isBrowserStorageAvailable()) return;
  localStorage.removeItem(PROFILE_STORAGE_KEYS.primaryToken);
  localStorage.removeItem(PROFILE_STORAGE_KEYS.activeToken);
  sessionStorage.removeItem(PROFILE_STORAGE_KEYS.primaryToken);
};
